import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import * as DocumentToolbar from "@/components/DocumentToolbar";
import AddedArtifactCard from "@/components/evidenceGathering/AddedArtifactCard";
import { ARTIFACT_TYPES, normalizeArtifactType } from "@/components/evidenceGathering/ArtifactTypeBadge";
import type { Artifact } from "@/services/projectsApi";

interface AddedArtifactListProps {
  /** The project's highest upload version, so each card can say whether the
   *  latest upload is what put it there. */
  latestUploadVersion?: number;
  artifacts: Artifact[];
  projectId: string;
  onView: (artifact: Artifact) => void;
  onRemove: (artifact: Artifact) => void;
  removingUids?: Set<string>;
}

const UNGROUPED = "Artefacts";
const ALL = "All";

// Plural label for the type tabs. The register's vocabulary is singular, and
// "Tables (10)" reads better than "Table (10)" on a count.
const plural = (type: string) => (type === "Sheet" ? "Sheets" : `${type}s`);

const AddedArtifactList = ({
  artifacts,
  projectId,
  onView,
  onRemove,
  removingUids,
  latestUploadVersion = 0,
}: AddedArtifactListProps) => {
  const [search, setSearch] = useState("");
  const [activeType, setActiveType] = useState(ALL);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const typeOf = (artifact: Artifact) => normalizeArtifactType(artifact.type) ?? artifact.type;

  // Only offer types actually present — a tab for a type with no artifacts is
  // a dead end. Ordered by the register's vocabulary, not by first sighting.
  const tabs = useMemo(() => {
    const counts = new Map<string, number>();
    for (const artifact of artifacts) {
      const type = typeOf(artifact);
      counts.set(type, (counts.get(type) ?? 0) + 1);
    }
    const ordered = Object.keys(ARTIFACT_TYPES).filter((type) => counts.has(type));
    return [
      { key: ALL, label: ALL, count: artifacts.length },
      ...ordered.map((type) => ({ key: type, label: plural(type), count: counts.get(type)! })),
    ];
  }, [artifacts]);

  // Filtered synchronously with no debounce, matching every other list here.
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return artifacts.filter((artifact) => {
      if (activeType !== ALL && typeOf(artifact) !== activeType) return false;
      if (!query) return true;
      return [artifact.name, artifact.uid, artifact.section, artifact.description]
        .filter(Boolean)
        .some((field) => field!.toLowerCase().includes(query));
    });
  }, [artifacts, search, activeType]);

  // Grouped by the sheet the artifacts came from — the only grouping the
  // Appendix register actually gives us.
  const sections = useMemo(() => {
    const bySheet = new Map<string, { label: string; tabNumber: number; items: Artifact[] }>();

    for (const artifact of filtered) {
      const sheetKey = artifact.sheetCodeName || artifact.section || UNGROUPED;
      let sheet = bySheet.get(sheetKey);
      if (!sheet) {
        sheet = {
          label: artifact.section || artifact.sheetCodeName || UNGROUPED,
          tabNumber: artifact.tabNumber ?? Number.MAX_SAFE_INTEGER,
          items: [],
        };
        bySheet.set(sheetKey, sheet);
      }
      sheet.items.push(artifact);
    }

    return Array.from(bySheet.entries())
      .map(([key, sheet]) => ({ key, ...sheet }))
      .sort((a, b) => a.tabNumber - b.tabNumber);
  }, [filtered]);

  const toggle = (key: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const Chevron = ({ open }: { open: boolean }) =>
    open ? (
      <ChevronDown className="w-4 h-4 shrink-0 text-muted-foreground" />
    ) : (
      <ChevronRight className="w-4 h-4 shrink-0 text-muted-foreground" />
    );

  return (
    <div>
      <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
        <div className="flex items-center gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveType(tab.key)}
              className={`text-sm transition-colors ${
                activeType === tab.key
                  ? "text-foreground font-semibold"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* The same search the document lists use, rather than a restyled
            copy of it — this one had drifted to a pill shape nothing else in
            the app uses. */}
        <DocumentToolbar.Search
          value={search}
          onChange={setSearch}
          placeholder="Search artefacts..."
          className="w-full sm:w-64"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="py-12 text-center text-sm text-muted-foreground">
          No artefacts match your search.
        </p>
      ) : (
        <div className="space-y-6">
          {sections.map((section) => {
            const sectionOpen = !collapsed.has(section.key);
            return (
              <div key={section.key}>
                <button
                  type="button"
                  onClick={() => toggle(section.key)}
                  className="flex items-center gap-1.5 text-left mb-3"
                >
                  <Chevron open={sectionOpen} />
                  <span className="text-sm font-semibold text-foreground">{section.label}</span>
                </button>

                {sectionOpen && (
                  <div className="flex flex-wrap gap-4 pl-5">
                    {section.items.map((artifact) => (
                      <AddedArtifactCard
                        key={artifact.id}
                        artifact={artifact}
                        projectId={projectId}
                        onView={() => onView(artifact)}
                        onRemove={() => onRemove(artifact)}
                        isRemoving={removingUids?.has(artifact.uid) ?? false}
                        latestUploadVersion={latestUploadVersion}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AddedArtifactList;
